import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import type { PaymentSavedDto } from '@tiffinify/contracts';
import { formatDay } from '@tiffinify/core';
import { PageLoader, Card, ErrorText, Field, ToggleRow, LoadError } from '../components/ui';
import { Av, Badge as PBadge, Btn, Card as PCard, Empty as PEmpty, IB, KV, Kick, Note, Row, Screen, Seg, Sp, Top } from '../components/p';
import { post, isNotFound } from '../lib/api';
import { endLabel, errorMessage, formatInr, mealsLabel } from '../lib/format';
import { useBrief, useMe } from '../lib/queries';

export function Payment() {
  const { subId = '' } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const today = me.data?.today ?? '';
  const q = useBrief(subId);
  const b = q.data;
  const returnTo = me.data?.user.role === 'owner' ? '/revenue' : (b ? `/s/${b.subscriberId}` : '/');
  // Untouched fields show what is due and today, from the first frame: no fill-in after render, no jump.
  const [typedAmount, setTypedAmount] = useState<string | null>(null);
  const [method, setMethod] = useState<'upi' | 'cash'>('upi');
  const [pickedDate, setPickedDate] = useState<string | null>(null);
  const [receipt, setReceipt] = useState(true);
  const amount = typedAmount ?? (b ? String(b.due) : '');
  const date = pickedDate ?? today;

  const n = Number(amount.replace(/[^\d]/g, ''));
  const save = useMutation({
    mutationFn: () => post<PaymentSavedDto>(`/subscriptions/${subId}/payments`, { amount: n, method, paidOn: date }),
    onSuccess: (res) => {
      void qc.invalidateQueries();
      const first = b!.name.split(' ')[0] ?? b!.name;
      nav(`/s/${b!.subscriberId}`, {
        state: { flash: { text: res.state.due === 0 ? `${formatInr(res.payment.amount)} saved. ${first} is paid up.` : `${formatInr(res.payment.amount)} saved. ${formatInr(res.state.due)} still due.`, ...(receipt ? { wa: { label: `Receipt to ${first}`, link: res.receipt.link } } : {}) } },
      });
    },
  });

  const head = (
    <Top>
      <div className="p-stack"><Kick>Cash or UPI</Kick><h1 className="p-h1">Record a payment</h1></div>
      <Sp /><IB icon="close" label="Close" to={returnTo} />
    </Top>
  );
  if (q.isPending) return <Screen gap={16}>{head}<PageLoader label="Adding up payments" /></Screen>;
  if (q.isError && !isNotFound(q.error)) return <Screen gap={16}>{head}<LoadError what="this plan" onRetry={() => void q.refetch()} /></Screen>;
  if (!b) return <Screen gap={16}>{head}<PCard><PEmpty title="Plan not found" /></PCard></Screen>;

  const balance = Math.max(0, b.due - n);
  const over = n > b.due;
  const first = b.name.split(' ')[0] ?? b.name;

  return (
    <Screen gap={16}>
      {head}
      <PCard>
        <Row to={`/s/${b.subscriberId}`} left={<Av name={b.name} />} t={b.name}
          s={`${mealsLabel(b.slot)} plan · ${formatInr(b.paid)} paid of ${formatInr(b.price)} · ${b.state === 'closed' ? 'closed' : b.state === 'ended' ? 'ended' : endLabel(b.daysToEnd, b.endDate).replace(/^E/, 'e')}`}
          right={b.due > 0 ? <PBadge tone="s">{formatInr(b.due)} due</PBadge> : <PBadge tone="g">Paid up</PBadge>} />
      </PCard>

      <div className="two date-pair">
        <Field label="Amount received" id="p-amount" value={amount} onChange={(v) => setTypedAmount(v.replace(/[^\d]/g, ''))} prefix="₹" inputMode="numeric" error={over ? `Only ${formatInr(b.due)} is due` : undefined} />
        <Field label="Date" id="p-date" type="date" value={date} onChange={setPickedDate} max={today || undefined} />
      </div>
      <Seg label="Paid by" value={method} onChange={setMethod} options={[{ value: 'cash', label: 'Cash' }, { value: 'upi', label: 'UPI' }]} />
      {b.due === 0 && <Note>Nothing is due on this plan, so there is nothing to record.</Note>}

      <PCard pad style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <KV k="Balance after" v={over ? '—' : balance === 0 && n > 0 ? `${formatInr(0)} · paid up` : formatInr(balance)} />
        <KV k="Plan ends" v={formatDay(b.endDate)} />
      </PCard>

      <Card className="toggle-card"><ToggleRow title={`Send a receipt to ${first}`} sub="On WhatsApp, with the amount and balance" on={receipt} onChange={setReceipt} /></Card>
      <ErrorText>{save.error ? errorMessage(save.error) : ''}</ErrorText>
      <Btn busy={save.isPending} label={receipt ? 'Save · send the receipt' : 'Save payment'} go={receipt ? 'send' : 'check'} onClick={() => save.mutate()} disabled={save.isPending || n < 1 || over || !date || b.due === 0} />
    </Screen>
  );
}
